import * as React from 'react'

import { resolveFooterConfig } from '@/lib/header-footer-config'
import { cn } from '@/lib/utils'
import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'

import EdunodesPoweredBy from '@/components/layouts/edunodes-powered-by'
import FooterLinkGroups from '@/components/layouts/footer/footer-link-groups'
import { FooterSocialIconLinks } from '@/components/layouts/footer/footer-social-icons'

import { FooterConfig } from '@/types/header-footer-config'

function FooterCopyright({
  className,
  showLogo = true,
  showSocialLinks = false,
}: React.HTMLAttributes<HTMLElement> & { showLogo?: boolean; showSocialLinks?: boolean }) {
  const { copyrightHolder, hasImageLogo, logoUrl, logoElementSmall } = useBranding()
  const logoAlt = copyrightHolder ? `${copyrightHolder} logo` : 'Brand logo'

  return (
    <div className={cn(className)}>
      <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          {showLogo &&
            (hasImageLogo ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={logoUrl} alt={logoAlt} className="h-6 w-auto max-w-40 shrink-0 object-contain" />
            ) : (
              logoElementSmall
            ))}
          <p className="text-sm text-muted-foreground">
            {`© ${new Date().getFullYear()} ${copyrightHolder}. All rights reserved.`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 sm:min-w-0">
          <FooterSocialIconLinks showSocialLinks={showSocialLinks} />
          <EdunodesPoweredBy />
        </div>
      </div>
    </div>
  )
}

const footerContainerClassNames: Record<NonNullable<FooterConfig['containerWidth']>, string> = {
  content: 'container',
  wide: 'mx-auto w-full max-w-6xl px-4 md:px-6',
  full: 'w-full px-4 md:px-6',
}

const footerSpacingClassNames: Record<NonNullable<FooterConfig['spacing']>, string> = {
  compact: 'py-3',
  normal: 'py-4',
  relaxed: 'py-6',
}

interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  footerConfig?: FooterConfig
}

function Footer({ className, footerConfig }: FooterProps) {
  const { text, lang } = useLocale()
  const resolvedFooterConfig = resolveFooterConfig(footerConfig, text, lang)
  const { copyrightHolder } = useBranding()

  if (resolvedFooterConfig.layoutPreset === 'minimal-general-v1') {
    return (
      <footer className={cn('bg-[var(--gv1-bg-primary)] py-8', className)}>
        <div className="mx-auto flex w-full max-w-[1232px] flex-col items-center justify-between gap-4 px-4 md:flex-row md:px-6">
          <p className="text-sm text-[var(--gv1-text-muted)]">
            {`© ${new Date().getFullYear()} ${copyrightHolder}. All rights reserved.`}
          </p>
          <FooterSocialIconLinks showSocialLinks={resolvedFooterConfig.showSocialLinks} />
        </div>
      </footer>
    )
  }

  return (
    <footer className={cn(resolvedFooterConfig.showBorder && 'border-t', className)}>
      {resolvedFooterConfig.showLinkGroups && (
        <FooterLinkGroups
          groups={resolvedFooterConfig.linkGroups}
          containerWidth={resolvedFooterConfig.containerWidth}
          spacing={resolvedFooterConfig.spacing}
        />
      )}
      {resolvedFooterConfig.showCopyright && (
        <FooterCopyright
          showLogo={resolvedFooterConfig.showLogo}
          showSocialLinks={resolvedFooterConfig.showSocialLinks}
          className={cn(
            footerContainerClassNames[resolvedFooterConfig.containerWidth],
            footerSpacingClassNames[resolvedFooterConfig.spacing]
          )}
        />
      )}
      {!resolvedFooterConfig.showCopyright && (
        <div
          className={cn(
            footerContainerClassNames[resolvedFooterConfig.containerWidth],
            footerSpacingClassNames[resolvedFooterConfig.spacing]
          )}
        >
          <EdunodesPoweredBy />
        </div>
      )}
    </footer>
  )
}

export default Footer
